import { RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../../context/ToastContext';
import adminWhatsAppService from '../../services/adminWhatsAppService';
import { formatShortDateTime, WHATSAPP_TEMPLATE_STATUS } from '../../utils/whatsapp';
import LoadingScreen from '../LoadingScreen';
import TemplateGuide from './TemplateGuide';
import TemplateList from './TemplateList';
import TemplatePreview from './TemplatePreview';

/** Sample values for the preview: the first placeholder is a first name, the rest are numbered. */
const sampleParams = (count) => Array.from({ length: count }, (_, i) => (i === 0 ? 'Priya' : `Sample ${i + 1}`));

const NOT_CONFIGURED =
  'WhatsApp is not configured yet. Ask the developer to set the WHATSAPP_* environment variables, then click Sync.';

/** The most recent synced_at across templates, or ''. ISO strings sort as text. */
const lastSynced = (templates) =>
  formatShortDateTime(
    templates
      .map((t) => t.synced_at)
      .filter(Boolean)
      .sort()
      .pop(),
  );

/**
 * Templates tab: the Meta templates we mirror, a phone preview of the selected one with sample
 * values, "Sync from Meta", and a short guide for creating a template.
 */
const TemplatesTab = () => {
  const toast = useToast();
  const [templates, setTemplates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notConfigured, setNotConfigured] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const latest = useRef(0);
  const syncingRef = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      latest.current += 1;
    };
  }, []);

  const load = useCallback(async () => {
    const ticket = ++latest.current;
    setLoading(true);
    try {
      const data = await adminWhatsAppService.listTemplates();
      if (ticket !== latest.current) return;
      setTemplates(data);
      setError('');
    } catch (err) {
      if (ticket !== latest.current) return;
      if (err.status === 503) setNotConfigured(true);
      else setError(err.message);
    } finally {
      if (ticket === latest.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // A failed status call is not worth a message: sync would still report the real problem.
    (async () => {
      try {
        const status = await adminWhatsAppService.getStatus();
        if (mounted.current && status?.configured === false) setNotConfigured(true);
      } catch {
        // deliberately quiet
      }
    })();
  }, [load]);

  const sync = async () => {
    if (syncingRef.current || notConfigured) return;
    syncingRef.current = true;
    setSyncing(true);
    try {
      const result = await adminWhatsAppService.syncTemplates();
      if (!mounted.current) return;
      if (result.total === 0) {
        toast.warning('WhatsApp returned no templates. Create one in WhatsApp Manager first, then sync again.');
      } else {
        const skipped = result.skipped ? `, ${result.skipped} skipped` : '';
        toast.success(`Templates synced: ${result.created} new, ${result.updated} updated${skipped}`);
      }
      await load();
    } catch (err) {
      if (!mounted.current) return;
      if (err.status === 503) setNotConfigured(true);
      else toast.error(err.message);
    } finally {
      syncingRef.current = false;
      if (mounted.current) setSyncing(false);
    }
  };

  const list = templates ?? [];
  const selected =
    list.find((t) => t.id === selectedId) ??
    list.find((t) => t.status === WHATSAPP_TEMPLATE_STATUS.APPROVED) ??
    list[0];
  const synced = lastSynced(list);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-medium text-foreground">Templates</h2>
          {synced && <p className="text-sm text-muted-foreground">Last synced {synced}</p>}
        </div>
        <button
          type="button"
          onClick={sync}
          disabled={syncing || notConfigured}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50"
        >
          <RefreshCw size={16} className={syncing ? 'animate-spin' : ''} />
          Sync from Meta
        </button>
      </div>

      {notConfigured && (
        <div
          role="status"
          className="px-4 py-3 text-sm text-amber-900 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-900 rounded-lg"
        >
          {NOT_CONFIGURED}
        </div>
      )}

      <TemplateGuide />

      {templates === null && loading && <LoadingScreen />}

      {templates === null && !loading && error && (
        <div className="p-6 text-center bg-card border border-border rounded-lg">
          <p className="font-medium text-foreground">Could not load templates</p>
          <p className="mt-1 text-sm text-muted-foreground break-words">{error}</p>
          <button
            type="button"
            onClick={load}
            className="mt-4 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
          >
            Retry
          </button>
        </div>
      )}

      {templates !== null && list.length === 0 && !notConfigured && (
        <div className="p-6 text-center bg-card border border-border rounded-lg">
          <p className="font-medium text-foreground">No templates yet</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Create one by following the guide above, then click Sync from Meta to bring it in.
          </p>
        </div>
      )}

      {list.length > 0 && (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
          <TemplateList templates={list} selectedId={selected.id} onSelect={setSelectedId} />
          <div className="min-w-0">
            <TemplatePreview template={selected} params={sampleParams(selected.variable_count || 0)} />
            <p className="mt-2 text-xs text-center text-muted-foreground">Preview uses sample values</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplatesTab;
